import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, StyleSheet, Text, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { spacing } from '../../../shared/theme/spacing';
import { showError } from '../../../shared/ui/appToast'
import { useOperationalConfirm } from '../../../shared/ui/useOperationalConfirm';
import { dispatchOrder } from '../../infra/deliveryTrackingRepo';
import { useEmployeesStore, withSelfOwner } from '../../../team/store/useEmployeesStore';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';

type Props = {
  orderId: string;
  onDispatched?: () => void;
};

/** Despacho delivery propio + quién lleva (auth user del empleado). */
export function DeliveryAssignPicker({ orderId, onDispatched }: Props) {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const employees = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);
  const [loading, setLoading] = useState(false);
  const [selectedAuthUserId, setSelectedAuthUserId] = useState<string | null>(null);
  const { confirm, ConfirmModal } = useOperationalConfirm();

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  const branchEmployees = useMemo(() => {
    const assigned = !branchId
      ? []
      : employees.filter(
          (e) =>
            e.isActive !== false &&
            Boolean(e.authUserId) &&
            e.branchAssignments.some((a) => a.branchId === branchId)
        );
    // El dueño no lleva EBP: entra como opción propia ("Vos" / su nombre).
    return withSelfOwner(assigned, selfOwner);
  }, [employees, selfOwner, branchId]);

  const handleDispatch = useCallback(async () => {
    if (branchEmployees.length > 0 && !selectedAuthUserId) {
      showError('Quién lleva', 'Elegí quién lo lleva.');
      return;
    }
    const ok = await confirm({
      title: 'Despachar pedido',
      message: selectedAuthUserId
        ? '¿Lo mandás con esta persona?'
        : '¿Confirmás que va en camino?',
      confirmLabel: 'Sí, despachar',
    });
    if (!ok) return;
    setLoading(true);
    try {
      await dispatchOrder(orderId, selectedAuthUserId ?? undefined);
      onDispatched?.();
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'orders', op: 'delivery.dispatch', error: e, userMessage: 'No se pudo despachar.' });
    } finally {
      setLoading(false);
    }
  }, [branchEmployees.length, confirm, orderId, onDispatched, selectedAuthUserId]);

  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="small" color={ticketColors.gold} />
        <Text style={styles.loadingText}>Despachando…</Text>
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      {branchEmployees.length > 0 ? (
        <View style={styles.list}>
          <Text style={styles.label}>Quién lleva</Text>
          {branchEmployees.map((emp) => {
            const id = emp.authUserId!;
            const selected = selectedAuthUserId === id;
            return (
              <TouchableOpacity
                key={emp.id}
                style={[styles.empRow, selected && styles.empSelected]}
                onPress={() => setSelectedAuthUserId(id)}
                accessibilityRole="button"
                accessibilityState={{ selected }}
              >
                <Ionicons
                  name={selected ? 'radio-button-on' : 'radio-button-off'}
                  size={18}
                  color={selected ? ticketColors.gold : ticketColors.ink2}
                />
                <Text style={styles.empName}>{emp.name}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ) : null}
      <TouchableOpacity style={styles.dispatchButton} onPress={() => void handleDispatch()} disabled={loading}>
        <Ionicons name="bicycle-outline" size={22} color={ticketColors.gold} />
        <Text style={styles.dispatchText}>Despachar pedido</Text>
      </TouchableOpacity>
      {ConfirmModal}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: spacing.sm, paddingVertical: spacing.sm },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
  },
  loadingText: {
    marginLeft: spacing.sm,
    color: ticketColors.ink2,
    fontSize: 14,
  },
  list: { gap: 6 },
  label: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  empRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 8,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  empSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  empName: { fontSize: 14, fontWeight: '600', color: ticketColors.ink },
  dispatchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: ticketColors.brandSoft,
    borderRadius: 10,
    paddingVertical: spacing.md,
    borderWidth: 1,
    borderColor: ticketColors.gold,
  },
  dispatchText: {
    color: ticketColors.gold,
    fontSize: 15,
    fontWeight: '600',
  },
});
